import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Collapse,
  IconButton,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { printOrDownloadPdf } from '../utils/printUtils';
import { CANADIAN_PROVINCES } from '../constants/canadianProvinces';
import { 
  isValidCanadianPostalCode, 
  formatCanadianPostalCode, 
  cleanPostalCodeForStorage,
  getPostalCodeValidationError 
} from '../utils/postalCodeValidation';
import { 
  isValidEmail, 
  getEmailValidationError, 
  formatEmail 
} from '../utils/emailValidation';

const InvoiceForm = ({ editMode = false, invoiceData = null }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customerName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    province: '',
    vin: '',
    carYear: '',
    carMake: '',
    carModel: '',
    licensePlate: '',
    odometerMileage: 0,
    claimNumber: '',
    insurerName: '',
    notes: ''
  });

  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [repairItems, setRepairItems] = useState([]);
  const [newItem, setNewItem] = useState({
    description: '',
    quantity: 1,
    unitPrice: '',
    category: 'Labor'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [existingCustomer, setExistingCustomer] = useState(null);
  const [customerLookupLoading, setCustomerLookupLoading] = useState(false);
  const [customerLookupTimeout, setCustomerLookupTimeout] = useState(null);
  const [notesExpanded, setNotesExpanded] = useState(true);
  
  // Autocomplete state for repair item descriptions
  const [descriptionSuggestions, setDescriptionSuggestions] = useState([]);
  const [descriptionLoading, setDescriptionLoading] = useState(false);
  const [descriptionSearchTimeout, setDescriptionSearchTimeout] = useState(null);
  
  // Postal code validation state
  const [postalCodeError, setPostalCodeError] = useState('');
  
  // Email validation state
  const [emailError, setEmailError] = useState('');
  
  // Required field validation state
  const [phoneError, setPhoneError] = useState('');
  const [customerNameError, setCustomerNameError] = useState('');
  
  // VIN decoded information dialog state
  const [vinInfoDialogOpen, setVinInfoDialogOpen] = useState(false);


  // Populate form with existing data when in edit mode
  useEffect(() => {
    if (editMode && invoiceData) {
      console.log('Populating form with invoice data:', invoiceData);

      // Populate customer information
      if (invoiceData.customer) {
        setFormData({
          id: invoiceData.id,
          customerName: invoiceData.customer.name || '',
          phone: invoiceData.customer.phone || '',
          email: invoiceData.customer.email || '',
          address: invoiceData.customer.address || '',
          city: invoiceData.customer.city || '',
          postalCode: invoiceData.customer.postalCode || '',
          province: invoiceData.customer.province || '',
          vin: invoiceData.vehicle?.vin || '',
          carYear: invoiceData.vehicle?.year || '',
          carMake: invoiceData.vehicle?.make || '',
          carModel: invoiceData.vehicle?.model || '',
          licensePlate: invoiceData.vehicle?.licensePlate || '',
          odometerMileage: invoiceData.vehicle?.odometerMileage ? parseInt(invoiceData.vehicle.odometerMileage) : 0,
          claimNumber: invoiceData.claimNumber || '',
          insurerName: invoiceData.insurerName || '',
          notes: invoiceData.notes || ''
        });
      } else {
        // Fallback to direct invoice data
        setFormData({
          id: invoiceData.id,
          customerName: invoiceData.customerName || '',
          phone: invoiceData.phone || '',
          email: invoiceData.email || '',
          address: invoiceData.address || '',
          city: invoiceData.city || '',
          postalCode: invoiceData.postalCode || '',
          province: invoiceData.province || '',
          vin: invoiceData.vin || '',
          carYear: invoiceData.carYear || '',
          carMake: invoiceData.carMake || '',
          carModel: invoiceData.carModel || '',
          licensePlate: invoiceData.licensePlate || '',
          odometerMileage: invoiceData.odometerMileage ? parseInt(invoiceData.odometerMileage) : 0,
          claimNumber: invoiceData.claimNumber || '',
          insurerName: invoiceData.insurerName || '',
          notes: invoiceData.notes || ''
        });
      }

      // Populate repair items if they exist
      if (invoiceData.repairItems && invoiceData.repairItems.length > 0) {
        setRepairItems(invoiceData.repairItems.map(item => ({
          ...item,
          id: item.id || Date.now() + Math.random(),
          totalPrice: item.unitPrice * item.quantity
        })));
      }
    }
  }, [editMode, invoiceData]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (descriptionSearchTimeout) {
        clearTimeout(descriptionSearchTimeout);
      }
      if (customerLookupTimeout) {
        clearTimeout(customerLookupTimeout);
      }
    };
  }, [descriptionSearchTimeout, customerLookupTimeout]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle odometer mileage as integer or null
    let processedValue = value;
    if (name === 'odometerMileage') {
      processedValue = value === '' ? 0 : parseInt(value) || 0;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Validate required fields
    if (name === 'phone') {
      if (!value || value.trim() === '') {
        setPhoneError('Phone number is required');
      } else {
        setPhoneError('');
      }
      
      // Check for existing customer when phone number changes (with debouncing)
      // Clear any existing timeout
      if (customerLookupTimeout) {
        clearTimeout(customerLookupTimeout);
      }

      // Set a new timeout to delay the API call
      const timeout = setTimeout(() => {
        if (value.length >= 10) {
          checkExistingCustomer(value);
        } else {
          // Clear existing customer if phone number is too short
          setExistingCustomer(null);
          // Clear address fields when customer is cleared
          setFormData(prev => ({
            ...prev,
            address: '',
            city: '',
            postalCode: '',
            province: ''
          }));
        }
      }, 500); // Wait 500ms after user stops typing

      setCustomerLookupTimeout(timeout);
    }
    
    if (name === 'customerName') {
      if (!value || value.trim() === '') {
        setCustomerNameError('Customer name is required');
      } else {
        setCustomerNameError('');
      }
    }
  };

  const handlePostalCodeChange = (e) => {
    const { value, selectionStart } = e.target;
    
    // Convert to uppercase and limit to 7 characters (6 + space)
    let processedValue = value.toUpperCase().slice(0, 7);
    
    // Handle backspace/delete operations
    const currentValue = formData.postalCode || '';
    const isDeleting = processedValue.length < currentValue.length;
    
    if (isDeleting) {
      // User is deleting - don't auto-add space, just process what they typed
      const formatted = formatCanadianPostalCode(processedValue);
      setFormData(prev => ({ ...prev, postalCode: formatted }));
    } else {
      // User is adding characters - auto-format as they type
      if (processedValue.length === 3 && !processedValue.includes(' ')) {
        processedValue = processedValue + ' ';
      }
      
      const formatted = formatCanadianPostalCode(processedValue);
      setFormData(prev => ({ ...prev, postalCode: formatted }));
    }
    
    // Validate postal code
    const error = getPostalCodeValidationError(processedValue);
    setPostalCodeError(error || '');
  };

  const handleEmailChange = (e) => {
    const { value } = e.target;
    
    // Format email (trim and lowercase)
    const formatted = formatEmail(value);
    setFormData(prev => ({ ...prev, email: formatted }));
    
    // Validate email
    const error = getEmailValidationError(formatted);
    setEmailError(error || '');
  };

  const checkExistingCustomer = async (phone) => {
    try {
      // Clear existing customer info first
      setExistingCustomer(null);
      setCustomerLookupLoading(true);

      // Make API call to backend to check for existing customer
      const response = await axios.get(API_ENDPOINTS.customerSearch(phone));

      if (response.data && response.data.length > 0) {
        const foundCustomer = response.data[0]; // Get the first matching customer

        setExistingCustomer(foundCustomer);

        // Auto-fill the form with existing customer data
        setFormData(prev => ({
          ...prev,
          customerName: foundCustomer.name || '',
          email: foundCustomer.email || '',
          address: foundCustomer.address || '',
          city: foundCustomer.city || '',
          postalCode: foundCustomer.postalCode || '',
          province: foundCustomer.province || ''
        }));

        console.log('Existing customer found and auto-filled:', foundCustomer);
      } else {
        console.log('No existing customer found for phone:', phone);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        // Customer not found - this is normal, not an error
        console.log('No existing customer found for phone:', phone);
      } else {
        console.error('Error checking existing customer:', err);
        // Don't show error to user for customer lookup failures
      }
    } finally {
      setCustomerLookupLoading(false);
    }
  };

  const handleVinChange = async (e) => {
    const vin = e.target.value;
    setFormData(prev => ({ ...prev, vin }));

    if (vin.length === 17) {
      try {
        setLoading(true);
        setError('');
        
        // Call the real NHTSA VIN decoder API
        const response = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/decodevin/${vin}?format=json`);
        
        if (!response.ok) {
          throw new Error('Failed to decode VIN');
        }
        
        const data = await response.json();
        
        if (data.Results && data.Results.length > 0) {
          // Extract relevant vehicle information from the API response
          const results = data.Results;
          
          const vehicleInfo = {
            make: results.find(r => r.Variable === 'Make')?.Value || '',
            model: results.find(r => r.Variable === 'Model')?.Value || '',
            year: results.find(r => r.Variable === 'Model Year')?.Value || '',
            bodyClass: results.find(r => r.Variable === 'Body Class')?.Value || '',
            engineModel: results.find(r => r.Variable === 'Engine Model')?.Value || '',
            transmissionStyle: results.find(r => r.Variable === 'Transmission Style')?.Value || '',
            fuelType: results.find(r => r.Variable === 'Fuel Type - Primary')?.Value || '',
            cylinders: results.find(r => r.Variable === 'Engine Number of Cylinders')?.Value || '',
            displacement: results.find(r => r.Variable === 'Displacement (L)')?.Value || '',
            plantCity: results.find(r => r.Variable === 'Plant City')?.Value || '',
            plantState: results.find(r => r.Variable === 'Plant State')?.Value || '',
            plantCountry: results.find(r => r.Variable === 'Plant Country')?.Value || ''
          };

          setVehicleInfo(vehicleInfo);

          // Auto-populate car fields with VIN decoded info
          setFormData(prev => ({
            ...prev,
            carYear: vehicleInfo.year || '',
            carMake: vehicleInfo.make || '',
            carModel: vehicleInfo.model || ''
          }));
        } else {
          throw new Error('No vehicle data found for this VIN');
        }
      } catch (err) {
        console.error('VIN decode error:', err);
        setError('Failed to decode VIN. Please check the VIN number and try again.');
        setVehicleInfo(null);
      } finally {
        setLoading(false);
      }
    } else {
      // Clear vehicle info if VIN is not complete
      setVehicleInfo(null);
      setError('');
    }
  };

  // Search for repair item descriptions
  const searchRepairItemDescriptions = async (searchTerm) => {
    if (searchTerm.length < 3) {
      setDescriptionSuggestions([]);
      return;
    }

    // Clear existing timeout
    if (descriptionSearchTimeout) {
      clearTimeout(descriptionSearchTimeout);
    }

    // Set loading state
    setDescriptionLoading(true);

    // Debounce the search by 300ms
    const timeout = setTimeout(async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.repairItemsSearch(searchTerm));
        setDescriptionSuggestions(response.data || []);
      } catch (err) {
        console.error('Error searching repair item descriptions:', err);
        setDescriptionSuggestions([]);
      } finally {
        setDescriptionLoading(false);
      }
    }, 300);

    setDescriptionSearchTimeout(timeout);
  };

  const addRepairItem = () => {
    if (newItem.description && newItem.unitPrice) {
      const item = {
        ...newItem,
        id: Date.now() + Math.random(), // Ensure unique ID
        unitPrice: parseFloat(newItem.unitPrice),
        totalPrice: parseFloat(newItem.unitPrice) * newItem.quantity
      };

      setRepairItems(prev => [...prev, item]);
      setNewItem({
        description: '',
        quantity: 1,
        unitPrice: '',
        category: 'Labor'
      });
    }
  };

  const removeRepairItem = (id) => {
    setRepairItems(prev => prev.filter(item => item.id !== id));
  };

  const updateRepairItem = (id, field, value) => {
    setRepairItems(prev => prev.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        // Recalculate total price
        updatedItem.totalPrice = updatedItem.unitPrice * updatedItem.quantity;
        return updatedItem;
      }
      return item;
    }));
  };

  const calculateSubtotal = () => {
    return repairItems.reduce((sum, item) => sum + item.totalPrice, 0);
  };

  const logCurrentState = () => {
    console.log('=== FORM STATE BEFORE SUBMISSION ===');
    console.log('Form Data:', formData);
    console.log('Repair Items:', repairItems);
    console.log('Subtotal:', calculateSubtotal());
    console.log('Vehicle Info:', vehicleInfo);
    console.log('Existing Customer:', existingCustomer);
    console.log('=====================================');
  };

  const handlePrint = async () => {
    if (!invoiceData?.id) {
      alert('Cannot print: Invoice not yet saved');
      return;
    }
    
    try {
      // Fetch PDF data
      const response = await axios.post(API_ENDPOINTS.invoicePdf(invoiceData.id), {}, {
        responseType: 'blob'
      });
      
      // Use the utility function to print or download
      const result = await printOrDownloadPdf(
        response.data, 
        `invoice-${invoiceData.invoiceNumber || 'draft'}.pdf`,
        { showProgress: true }
      );
      
      if (result.success) {
        if (result.method === 'print') {
          console.log('✅ Invoice printed successfully');
        } else {
          console.log('📥 Invoice downloaded successfully');
        }
      } else {
        alert('Failed to print or download invoice');
      }
      
    } catch (err) {
      console.error('Failed to print invoice:', err);
      alert('Failed to print invoice: ' + (err.response?.data || err.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear all field errors first
    setPhoneError('');
    setCustomerNameError('');
    setEmailError('');
    setPostalCodeError('');

    // Validate required fields - only customer name and phone are required
    if (!formData.phone || !formData.phone.trim()) {
      setPhoneError('Phone number is required');
      setError('Please fill in all required fields');
      return;
    }

    if (!formData.customerName || !formData.customerName.trim()) {
      setCustomerNameError('Customer name is required');
      setError('Please fill in all required fields');
      return;
    }

    // Validate that at least one repair item is present
    if (repairItems.length === 0) {
      setError('At least one repair item is required');
      return;
    }

    // Log current state before submission
    logCurrentState();

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const subtotal = calculateSubtotal();
      // Ensure odometer mileage is a valid integer or null if not provided
      // Clean postal code and email for database storage
      const processedFormData = {
        ...formData,
        odometerMileage: formData.odometerMileage === 0 || formData.odometerMileage === '' ? null : parseInt(formData.odometerMileage) || null,
        postalCode: cleanPostalCodeForStorage(formData.postalCode),
        email: formatEmail(formData.email)
      };

      const invoiceData = {
        ...processedFormData,
        subtotal: subtotal,
        repairItems: repairItems
      };

      console.log('Sending invoice data:', invoiceData);
      console.log('Current repair items to be saved:', repairItems);

      if (editMode && invoiceData.id) {
        // Update existing invoice
        const updateData = {
          notes: invoiceData.notes,
          subtotal: subtotal,
          // Vehicle fields
          vin: invoiceData.vin,
          carYear: invoiceData.carYear,
          carMake: invoiceData.carMake,
          carModel: invoiceData.carModel,
          licensePlate: invoiceData.licensePlate,
          odometerMileage: invoiceData.odometerMileage === 0 || invoiceData.odometerMileage === '' ? null : parseInt(invoiceData.odometerMileage) || null,
          claimNumber: invoiceData.claimNumber,
          insurerName: invoiceData.insurerName,
          repairItems: repairItems,
          // Include customer fields for update
          customerName: invoiceData.customerName,
          phone: invoiceData.phone,
          email: invoiceData.email,
          address: invoiceData.address,
          city: invoiceData.city,
          postalCode: invoiceData.postalCode,
          province: invoiceData.province
        };

        console.log('=== FRONTEND: SENDING UPDATE DATA ===');
        console.log('Update Data Object:', updateData);
        console.log('Update Data JSON:', JSON.stringify(updateData, null, 2));

        const response = await axios.put(API_ENDPOINTS.invoiceById(invoiceData.id), updateData);
        console.log('Update response:', response.data);
        setSuccess('Invoice updated successfully!');
        
        // Navigate back to invoices list after a short delay
        setTimeout(() => {
          navigate('/invoices');
        }, 1500);
      } else {
        // Create new invoice
        const response = await axios.post(API_ENDPOINTS.invoices, invoiceData);
        console.log('Create response:', response.data);
        setSuccess('Invoice created successfully!');
        
        // Navigate back to invoices list after a short delay
        setTimeout(() => {
          navigate('/invoices');
        }, 1500);
      }
    } catch (err) {
      console.error('Invoice submission error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save invoice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
    
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
          <CardHeader
            title={
              editMode
                ? `Edit Invoice - ${formData.customerName ? '[ ' + formData.customerName + ' ]' : ''}`
                : `Create New Invoice - ${formData.customerName ? formData.customerName  : ''}`
            }
            titleTypographyProps={{ variant: 'h', fontWeight: 'bold' }}
            sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
          />
          <CardContent>
            {/* Required Fields Note */}
            
            {/* Existing Customer Alert */}
            {existingCustomer && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Existing Customer Found!</strong> Customer details have been auto-filled.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ ml: 2 }}
                  onClick={() => {
                    setExistingCustomer(null);
                    setFormData(prev => ({
                      ...prev,
                      customerName: '',
                      email: '',
                      address: '',
                      city: '',
                      postalCode: '',
                      province: ''
                    }));
                  }}
                >
                  Clear & Create New
                </Button>
              </Alert>
            )}

            {/* Two Column Layout */}
            <Grid container spacing={1}>
              {/* Column 1: Customer Details */}
              <Grid item xs={12} md={6} >
                <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Customer Information
                </Typography>

                {/* Row 1: Phone | Name */}
                <Grid container spacing={2} >
                  <Grid item xs={12} md={4}>

                    <TextField
                      fullWidth
                      label="Phone Number *"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Phone"
                      required
                      size="small"
                      error={!!phoneError}
                      helperText={phoneError || " "}
                      slotProps={{
                        input: {
                          endAdornment: customerLookupLoading && (
                            <CircularProgress size={20} />
                          )
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Customer Name *"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleInputChange}
                      placeholder="Name"
                      required
                      size="small"
                      error={!!customerNameError}
                      helperText={customerNameError || " "}
                    />
                  </Grid>
                </Grid>

                {/* Row 2: Email | Address */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleEmailChange}
                      placeholder="example@email.com"
                      size="small"
                      error={!!emailError}
                      helperText={emailError || " "}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Street Address"
                      size="small"
                    />
                  </Grid>
                </Grid>

                {/* Row 3: City | Postal Code | Province */}
                  <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="City"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="City"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={2.5}>
                    <TextField
                      fullWidth
                      label="Postal Code"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handlePostalCodeChange}
                      placeholder="L6Y 0A6"
                      size="small"
                        error={!!postalCodeError}
                        helperText={postalCodeError || " "}
                        inputProps={{
                        maxLength: 7, // 6 characters + space
                        style: { textTransform: 'uppercase' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Province</InputLabel>
                      <Select
                        value={formData.province}
                        onChange={handleInputChange}
                        label="Province"
                        name="province"
                      >
                        <MenuItem value="">
                          <em>Select Province</em>
                        </MenuItem>
                        {CANADIAN_PROVINCES.map((province) => (
                          <MenuItem key={province.code} value={province.code}>
                            {province.code} - {province.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

               

              </Grid>

              {/* Column 2: Vehicle Details */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Vehicle Information
                </Typography>

                {/* Row 1: VIN | License Plate */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="VIN"
                      name="vin"
                      value={formData.vin}
                      onChange={handleVinChange}
                      maxLength={17}
                      size="small"
                      placeholder="VIN"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {loading && <CircularProgress size={20} />}
                              {vehicleInfo && (
                                <IconButton
                                  size="small"
                                  onClick={() => setVinInfoDialogOpen(true)}
                                  sx={{ 
                                    color: 'primary.main',
                                    '&:hover': { bgcolor: 'primary.100' },
                                    minWidth: 'auto',
                                    padding: '1px'
                                  }}
                                  title="View VIN Decoded Information"
                                >
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              )}
                            
                            </Box>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Lic. Plate"
                      name="licensePlate"
                      value={formData.licensePlate}
                      onChange={handleInputChange}
                      placeholder="Plate"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Odo. Mileage"
                      name="odometerMileage"
                      type="number"
                      value={formData.odometerMileage}
                      onChange={handleInputChange}
                      placeholder="Miles"
                      inputProps={{ min: 0, step: 1 }}
                      size="small"
                    />
                  </Grid>
                </Grid>

            

                {/* Row 2: Year | Make */}
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Car Year"
                      name="carYear"
                      type="number"
                      value={formData.carYear}
                      onChange={handleInputChange}
                      placeholder="Year"
                      inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Car Make"
                      name="carMake"
                      value={formData.carMake}
                      onChange={handleInputChange}
                      placeholder="Make"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Car Model"
                      name="carModel"
                      value={formData.carModel}
                      onChange={handleInputChange}
                      placeholder="Model"
                      size="small"
                    />
                  </Grid>
                </Grid>

                {/* Row 3: Model | Claim Number */}
                <Grid container spacing={1} sx={{ mb: 3 }}>
            
                  <Grid item xs={12} md={4.5}>
                    <TextField
                      fullWidth
                      label="Claim Number"
                      name="claimNumber"
                      value={formData.claimNumber}
                      onChange={handleInputChange}
                      placeholder="Claim #"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={4.5}>
                    <TextField
                      fullWidth
                      label="Insurer Name"
                      name="insurerName"
                      value={formData.insurerName}
                      onChange={handleInputChange}
                      placeholder="Insurance Company"
                      size="small"
                    />
                  </Grid>
                </Grid>   
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Repair Items Section */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
          <CardHeader
            title="Repair Items"
            titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
            sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
          />
          <CardContent>
            
            {/* Add New Item Form */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={7}>
                <Autocomplete
                  freeSolo
                  options={descriptionSuggestions}
                  value={newItem.description}
                  onInputChange={(event, newInputValue) => {
                    setNewItem(prev => ({ ...prev, description: newInputValue }));
                    searchRepairItemDescriptions(newInputValue);
                  }}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setNewItem(prev => ({ ...prev, description: newValue }));
                    }
                  }}
                  loading={descriptionLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Description"
                      placeholder="Describe the repair or service (type 3+ characters for suggestions)"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {descriptionLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  )}
                  noOptionsText={newItem.description.length >= 3 ? "No suggestions found" : "Type 3+ characters for suggestions"}
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <TextField
                  fullWidth
                  label="Qty"
                  type="number"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  inputProps={{ min: 1, step: 1 }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <TextField
                  fullWidth
                  label="Price"
                  type="number"
                  value={newItem.unitPrice}
                  onChange={(e) => setNewItem(prev => ({ ...prev, unitPrice: e.target.value }))}
                  placeholder="0.00"
                  inputProps={{ min: 0, step: 0.01 }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={newItem.category}
                    onChange={(e) => setNewItem(prev => ({ ...prev, category: e.target.value }))}
                    label="Category"
                  >
                    <MenuItem value="Labor">Labor</MenuItem>
                    <MenuItem value="Parts">Parts</MenuItem>
                    <MenuItem value="Materials">Materials</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={1}>
                <Button
                  variant="contained"
                  onClick={addRepairItem}
                  disabled={!newItem.description || !newItem.unitPrice}
                  size="small"
                  sx={{ height: '40px', minWidth: '40px' }}
                  title="Add repair item (description and price required)"
                >
                  <AddIcon />
                </Button>
              </Grid>
            </Grid>

            {/* Repair Items Table */}
            {repairItems.length > 0 && (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Unit Price</TableCell>
                      <TableCell>Total Price</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {repairItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>${item.unitPrice.toFixed(2)}</TableCell>
                        <TableCell>${item.totalPrice.toFixed(2)}</TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => removeRepairItem(item.id)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={3} align="right"><strong>Subtotal:</strong></TableCell>
                      <TableCell><strong>${calculateSubtotal().toFixed(2)}</strong></TableCell>
                      <TableCell colSpan={2}></TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            )}

            {/* Optional Notes Section */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
              <Grid container alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={10}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'medium' }}>
                    Notes (Optional)
                  </Typography>
                </Grid>
                <Grid item xs={2} sx={{ textAlign: 'right' }}>
                  <IconButton
                    aria-label="expand/collapse notes"
                    onClick={() => setNotesExpanded(!notesExpanded)}
                    size="small"
                  >
                    {notesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Grid>
              </Grid>
              <Collapse in={notesExpanded} timeout="auto" unmountOnExit>
                <TextField
                  fullWidth
                  label="Additional notes or special instructions"
                  multiline
                  rows={2}
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Enter any additional notes, special instructions, or comments..."
                  size="small"
                  variant="outlined"
                />
              </Collapse>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={() => navigate(editMode ? '/invoices' : '/')} size="small">
            {editMode ? 'Back to Invoices' : 'Cancel'}
          </Button>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {editMode && invoiceData?.id && (
              <Button
                variant="outlined"
                onClick={handlePrint}
                disabled={loading}
                size="small"
                startIcon={<span>🖨️</span>}
              >
                Print Invoice
              </Button>
            )}
            
            <Button
              type="submit"
              variant="contained"
              disabled={loading || repairItems.length === 0}
              size="small"
            >
              {loading ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Invoice' : 'Create Invoice')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* VIN Decoded Information Dialog */}
      <Dialog 
        open={vinInfoDialogOpen} 
        onClose={() => setVinInfoDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              VIN Decoded Information
            </Typography>
            <Button
              variant="outlined"
              size="small"
              href={`https://vpic.nhtsa.dot.gov/decoder/Decoder?VIN=${formData.vin}&ModelYear=`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: '0.75rem' }}
            >
              View on NHTSA
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {vehicleInfo && (
            <Grid container spacing={2}>
              {/* Basic Info Row */}
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Make:</strong> {vehicleInfo.make}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Model:</strong> {vehicleInfo.model}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Year:</strong> {vehicleInfo.year}
                </Typography>
              </Grid>
              
              {/* Engine Info Row */}
              {vehicleInfo.engineModel && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Engine:</strong> {vehicleInfo.engineModel}
                  </Typography>
                </Grid>
              )}
              {vehicleInfo.cylinders && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Cylinders:</strong> {vehicleInfo.cylinders}
                  </Typography>
                </Grid>
              )}
              {vehicleInfo.displacement && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Displacement:</strong> {vehicleInfo.displacement}L
                  </Typography>
                </Grid>
              )}
              
              {/* Additional Details Row */}
              {vehicleInfo.bodyClass && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Body:</strong> {vehicleInfo.bodyClass}
                  </Typography>
                </Grid>
              )}
              {vehicleInfo.transmissionStyle && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Transmission:</strong> {vehicleInfo.transmissionStyle}
                  </Typography>
                </Grid>
              )}
              {vehicleInfo.fuelType && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Fuel:</strong> {vehicleInfo.fuelType}
                  </Typography>
                </Grid>
              )}
              
              {/* Plant Info Row */}
              {vehicleInfo.plantCity && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Plant:</strong> {vehicleInfo.plantCity}, {vehicleInfo.plantState}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVinInfoDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceForm;
